import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const InvestmentsPage = ({ user, t, buildingId = null }) => {
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ 
    category: '',
    subcategory: '',
    status: 'planned', 
    start_date: '', 
    end_date: '', 
    description: '' 
  });

  const fetchInvestments = useCallback(async () => {
    try {
      const url = buildingId ? `${API}/investments?building_id=${buildingId}` : `${API}/investments`;
      const response = await axios.get(url, { withCredentials: true });
      setInvestments(response.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, t]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchInvestments();
  }, [user, navigate, fetchInvestments]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.category) {
      toast.error(t('investmentCategoryRequired'));
      return;
    }
    if (!formData.subcategory) {
      toast.error(t('investmentSubcategoryRequired'));
      return;
    }
    if (!formData.description || !formData.description.trim()) {
      toast.error(t('descriptionRequired'));
      return;
    }
    if (!formData.start_date) {
      toast.error(t('startDateRequired'));
      return;
    }
    // Validate end date is not before start date
    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      toast.error(t('endDateBeforeStartDate'));
      return;
    }
    
    try {
      // Generate a title from description for backward compatibility (first 50 chars)
      const payload = {
        ...formData,
        title: formData.description.substring(0, 50),
        completed_date: formData.end_date
      };
      
      const url = buildingId ? `${API}/investments?building_id=${buildingId}` : `${API}/investments`;
      await axios.post(url, payload, { withCredentials: true });
      toast.success(t('investmentCreated'));
      setShowModal(false);
      setFormData({ category: '', subcategory: '', status: 'planned', start_date: '', end_date: '', description: '' });
      fetchInvestments();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleDelete = async (investmentId) => {
    if (!window.confirm(t('deleteInvestment'))) return;
    try {
      const url = buildingId 
        ? `${API}/investments/${investmentId}?building_id=${buildingId}`
        : `${API}/investments/${investmentId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(t('investmentDeleted'));
      fetchInvestments();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Get investment categories for dropdown
  const getInvestmentCategoryOptions = () => {
    return [
      { key: 'physical', label: t('investmentCategories.physical') },
      { key: 'energy', label: t('investmentCategories.energy') },
      { key: 'security', label: t('investmentCategories.security') },
      { key: 'social', label: t('investmentCategories.social') },
      { key: 'legal', label: t('investmentCategories.legal') }
    ];
  };

  // Get investment subcategories based on selected category
  const getInvestmentSubcategoryOptions = (categoryKey) => {
    if (!categoryKey) return [];
    
    const subcategoriesMap = {
      physical: ['elevatorRenovation', 'roofRenovation', 'exteriorInsulation', 'parkingConstruction', 'earthquakeReinforcement', 'fireSystemInstallation', 'waterTankRenovation', 'generatorInstallation'],
      energy: ['solarPanels', 'heatPump', 'ledConversion', 'smartMeterSystems', 'insulationImprovements'],
      security: ['cameraSystem', 'accessCardSystem', 'smartBuildingInfrastructure', 'intercomRenovation', 'buildingManagementSoftware'],
      social: ['childrensPlayground', 'sportsAreaFitness', 'landscapingRenovation', 'pergolaSeatingAreas', 'commonAreaDecoration'],
      legal: ['architecturalDesign', 'structuralProject', 'municipalityPermit', 'condominiumTransition']
    };

    const subcategoryKeys = subcategoriesMap[categoryKey] || [];
    return subcategoryKeys.map(key => ({
      key: key,
      label: t(`investmentSubcategories.${categoryKey}.${key}`)
    }));
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    const statusLabels = {
      planned: t('planned'),
      in_progress: t('inProgress'),
      completed: t('completed')
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('investments')}</h1>
            </div>
            {isAdmin && (
              <button
                data-testid="create-investment-button"
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
              >
                {t('newInvestment')}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('title')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('description')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('date')}</th>
                  {isAdmin && <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {investments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      {t('noInvestmentsYet')}
                    </td>
                  </tr>
                ) : (
                  investments.map((investment) => (
                    <tr key={investment.id} data-testid={`investment-${investment.id}`}>
                      <td className="px-6 py-4 text-sm font-medium">{investment.title}</td>
                      <td className="px-6 py-4 text-sm">{getStatusBadge(investment.status)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{investment.description}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {investment.completed_date || investment.start_date || '-'}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => handleDelete(investment.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-investment-${investment.id}`}
                          >
                            {t('delete')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="create-investment-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newInvestment')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('investmentCategoryLabel')}</label>
                <select
                  data-testid="investment-category-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                >
                  <option value="">{t('investmentCategoryPlaceholder')}</option>
                  {getInvestmentCategoryOptions().map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('investmentSubcategoryLabel')}</label>
                <select
                  data-testid="investment-subcategory-select"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={!formData.category}
                  required
                >
                  <option value="">{t('investmentSubcategoryPlaceholder')}</option>
                  {getInvestmentSubcategoryOptions(formData.category).map(subcat => (
                    <option key={subcat.key} value={subcat.key}>{subcat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('status')}</label>
                <select
                  data-testid="status-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="planned">{t('planned')}</option>
                  <option value="in_progress">{t('inProgress')}</option>
                  <option value="completed">{t('completed')}</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('startDate')}</label>
                <input
                  type="date"
                  data-testid="start-date-input"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('endDate')}</label>
                <input
                  type="date"
                  data-testid="end-date-input"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  rows="3"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button 
                  type="submit" 
                  data-testid="submit-investment" 
                  disabled={!formData.category || !formData.subcategory || !formData.description?.trim() || !formData.start_date}
                  className={`flex-1 px-4 py-2 rounded-lg ${
                    !formData.category || !formData.subcategory || !formData.description?.trim() || !formData.start_date
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
